// src/components/EsquemaHero.tsx
//
// El esquema de "Todo conectado", en chico, para el hero: el link del
// consultorio al centro y lo que conecta alrededor. Pedido de Esteban
// (oct-2026): le gustó el esquema de la sección "Todo conectado" y lo quiso
// arriba, donde están las frases del subtítulo. El hero muestra primero las
// frases; cuando el titular llega a "todo", las frases se van y se arma esto
// (ver Hero.tsx).
//
// Acompaña al titular: cuando cambia la palabra, sale un pulso del link hacia
// la pieza que nombra y la pieza se enciende al llegar. Con "todo", todas.
//
// Escritorio (desde 1024 px): piezas a los costados, como en la sección.
// Celular y tablet: el link arriba y las piezas en dos columnas, colgadas de
// una línea central. Medidas fijas en px: así las líneas del SVG caen justo en
// los bordes de las tarjetas (anotadas junto a cada geometría; van con los
// tamaños de EsquemaHero.css).
//
// Es decorativo (aria-hidden): las frases siguen en la página, invisibles,
// para lectores de pantalla. Animaciones de transform/opacity, salvo los
// trazos del SVG (stroke-dashoffset), como en la sección.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ClipboardList, MapPin, Pill, CreditCard, Bell, Link2 } from 'lucide-react';
import './EsquemaHero.css';

export type IdPieza = 'turnos' | 'historias' | 'sedes' | 'recetas' | 'cobros' | 'recordatorios';

interface Pieza {
  id: IdPieza;
  titulo: string;
  icono: React.ReactNode;
  destacada?: boolean;
}

// Las tres de la izquierda de arriba abajo, después las tres de la derecha:
// el mismo orden que el esquema de la sección "Todo conectado".
const PIEZAS: Pieza[] = [
  { id: 'turnos', titulo: 'Turnos', icono: <CalendarDays /> },
  { id: 'historias', titulo: 'Historias clínicas', icono: <ClipboardList /> },
  { id: 'sedes', titulo: 'Sedes', icono: <MapPin /> },
  { id: 'recetas', titulo: 'Recetas electrónicas', icono: <Pill />, destacada: true },
  { id: 'cobros', titulo: 'Cobros', icono: <CreditCard /> },
  { id: 'recordatorios', titulo: 'Recordatorios', icono: <Bell /> },
];

export const TODAS_LAS_PIEZAS: IdPieza[] = PIEZAS.map((p) => p.id);

interface Trazo {
  d: string;
  orden: number; // para dibujarlas en orden al armarse
}

interface Geometria {
  ancho: number;
  alto: number;
  centro: { x: number; y: number };
  piezas: Array<{ x: number; y: number; desde: number }>; // centro de cada tarjeta; desde: de qué lado entra
  trazos: Trazo[];  // líneas tenues, fijas
  rutas: string[];  // por pieza: el camino del pulso, desde el link
  degradado: { x2: number; y2: number };
}

// ESCRITORIO: 880 × 168. Centro de 288 × 60 (bordes en x = 296 y 584) y
// tarjetas de 220 × 44 en tres filas (y = 22, 84, 146): las de la izquierda
// terminan en x = 220 y las de la derecha empiezan en x = 660.
const FILAS_ESC = [22, 84, 146];
const curvaEsc = (izquierda: boolean, y: number) => {
  const x0 = izquierda ? 296 : 584;
  const x1 = izquierda ? 220 : 660;
  const xc = (x0 + x1) / 2;
  return `M${x0},84 C${xc},84 ${xc},${y} ${x1},${y}`;
};
const ESCRITORIO: Geometria = {
  ancho: 880,
  alto: 168,
  centro: { x: 440, y: 84 },
  piezas: PIEZAS.map((_, n) => ({ x: n < 3 ? 110 : 770, y: FILAS_ESC[n % 3], desde: n < 3 ? 12 : -12 })),
  trazos: PIEZAS.map((_, n) => ({ d: curvaEsc(n < 3, FILAS_ESC[n % 3]), orden: n % 3 })),
  rutas: PIEZAS.map((_, n) => curvaEsc(n < 3, FILAS_ESC[n % 3])),
  degradado: { x2: 880, y2: 0 },
};

// CELULAR: 328 × 196 (entra justo en un teléfono de 360 de ancho). El link
// arriba (260 × 46) y debajo dos columnas de tarjetas de 152 × 42 (y = 77,
// 126, 175), colgadas de una línea central en x = 164: las ramas llegan hasta
// x = 152 (izquierda) y x = 176 (derecha).
const FILAS_CEL = [77, 126, 175];
const CELULAR: Geometria = {
  ancho: 328,
  alto: 196,
  centro: { x: 164, y: 23 },
  piezas: PIEZAS.map((_, n) => ({ x: n < 3 ? 76 : 252, y: FILAS_CEL[n % 3], desde: n < 3 ? 10 : -10 })),
  trazos: [
    { d: 'M164,46 V175', orden: 0 },
    ...PIEZAS.map((_, n) => ({ d: `M164,${FILAS_CEL[n % 3]} H${n < 3 ? 152 : 176}`, orden: 1 + (n % 3) })),
  ],
  rutas: PIEZAS.map((_, n) => `M164,46 V${FILAS_CEL[n % 3]} H${n < 3 ? 152 : 176}`),
  degradado: { x2: 0, y2: 196 },
};

interface VarianteProps {
  geo: Geometria;
  clase: string;
  idDegradado: string;
  activas: Set<IdPieza>;
  vuelta: number;
  todas: boolean;
}

const Variante: React.FC<VarianteProps> = ({ geo, clase, idDegradado, activas, vuelta, todas }) => (
  <div className={`eh-caja ${clase}`} style={{ width: geo.ancho, height: geo.alto }}>
    <svg className="eh-lineas" viewBox={`0 0 ${geo.ancho} ${geo.alto}`} width={geo.ancho} height={geo.alto}>
      <defs>
        {/* En coordenadas del esquema, no de cada línea: una línea recta no
            tiene alto (o ancho), y así el navegador no pinta el degradé. */}
        <linearGradient id={idDegradado} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={geo.degradado.x2} y2={geo.degradado.y2}>
          <stop offset="0" stopColor="#38bdf8" />
          <stop offset="0.5" stopColor="#22d3ee" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      {geo.trazos.map((t, n) => (
        <path
          key={n}
          d={t.d}
          pathLength={100}
          className="eh-linea"
          style={{ ['--i' as string]: t.orden }}
        />
      ))}
      {/* Un pulso por pieza encendida. La clave cambia en cada vuelta del
          titular: el trazo se vuelve a montar y la animación arranca de nuevo. */}
      {vuelta > 0 &&
        PIEZAS.map((p, n) =>
          activas.has(p.id) ? (
            <path
              key={`${p.id}-${vuelta}`}
              d={geo.rutas[n]}
              pathLength={100}
              className="eh-pulso"
              stroke={`url(#${idDegradado})`}
              style={todas ? { animationDelay: `${n * 40}ms` } : undefined}
            />
          ) : null
        )}
    </svg>

    <div className="eh-centro" style={{ left: geo.centro.x, top: geo.centro.y }}>
      <span className="eh-centro-icono"><Link2 /></span>
      <span className="eh-centro-texto">
        <span className="eh-centro-titulo">Tu consultorio</span>
        <span className="eh-centro-link">consultarapp.com/p/tu-nombre</span>
      </span>
    </div>

    {PIEZAS.map((p, n) => (
      <div
        key={p.id}
        className={`eh-pieza${p.destacada ? ' eh-pieza--destacada' : ''}${activas.has(p.id) ? ' is-activa' : ''}`}
        style={{
          left: geo.piezas[n].x,
          top: geo.piezas[n].y,
          ['--fila' as string]: n % 3,
          ['--desde' as string]: `${geo.piezas[n].desde}px`,
        }}
      >
        <span className="eh-pieza-icono">{p.icono}</span>
        <span className="eh-pieza-titulo">{p.titulo}</span>
      </div>
    ))}
  </div>
);

interface Props {
  // false: no se ve, pero ya ocupa su lugar (nada salta cuando aparece)
  visible: boolean;
  activas: ReadonlyArray<IdPieza>;
}

const EsquemaHero: React.FC<Props> = ({ visible, activas }) => {
  const clave = activas.join(',');
  const conjunto = useMemo(() => new Set(clave.split(',') as IdPieza[]), [clave]);
  const todas = conjunto.size === PIEZAS.length;

  // Cada cambio de palabra, ya armado el esquema, es una vuelta: dispara los
  // pulsos. Al armarse no hay pulso: las piezas aparecen ya encendidas.
  const [vuelta, setVuelta] = useState(0);
  const previa = useRef(clave);
  useEffect(() => {
    if (previa.current === clave) return;
    previa.current = clave;
    if (visible) setVuelta((v) => v + 1);
  }, [clave, visible]);

  return (
    <div className={`eh${visible ? ' is-armado' : ''}`} aria-hidden="true">
      <Variante geo={ESCRITORIO} clase="eh--esc" idDegradado="eh-trazo-esc" activas={conjunto} vuelta={vuelta} todas={todas} />
      <Variante geo={CELULAR} clase="eh--cel" idDegradado="eh-trazo-cel" activas={conjunto} vuelta={vuelta} todas={todas} />
    </div>
  );
};

export default EsquemaHero;
